import { useQuery } from '@tanstack/react-query';
import { api } from '@repo/api-sdk';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CreditCard,
  CheckCircle2,
  Clock,
  XCircle,
  RotateCcw,
  Layers,
  Receipt,
  ChevronRight,
  Calendar,
} from 'lucide-react';
import { PageLoader } from '@repo/ui';

// ── helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(amount: number, lang: string) {
  return new Intl.NumberFormat(lang === 'ar' ? 'ar-EG' : 'en-US', {
    style: 'currency',
    currency: 'EGP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount ?? 0);
}

function formatDate(dateStr: string, lang: string) {
  return new Date(dateStr).toLocaleDateString(lang === 'ar' ? 'ar-EG' : 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function getStatusMeta(status: string) {
  switch (status?.toLowerCase()) {
    case 'paid':
      return {
        label: 'paid',
        color: 'text-emerald-600 dark:text-emerald-400',
        bg: 'bg-emerald-500/10',
        border: 'border-emerald-500/20',
        barColor: '#10b981',
        icon: CheckCircle2,
      };
    case 'partially_paid':
      return {
        label: 'partial',
        color: 'text-amber-600 dark:text-amber-400',
        bg: 'bg-amber-500/10',
        border: 'border-amber-500/20',
        barColor: '#f59e0b',
        icon: Clock,
      };
    case 'unpaid':
      return {
        label: 'unpaid',
        color: 'text-rose-600 dark:text-rose-400',
        bg: 'bg-rose-500/10',
        border: 'border-rose-500/20',
        barColor: '#ef4444',
        icon: XCircle,
      };
    case 'refunded':
      return {
        label: 'refunded',
        color: 'text-blue-600 dark:text-blue-400',
        bg: 'bg-blue-500/10',
        border: 'border-blue-500/20',
        barColor: '#3b82f6',
        icon: RotateCcw,
      };
    default:
      return {
        label: status ?? 'unknown',
        color: 'text-muted-foreground',
        bg: 'bg-muted',
        border: 'border-border',
        barColor: '#94a3b8',
        icon: Clock,
      };
  }
}

function ProgressBar({ paid, total, color }: { paid: number; total: number; color: string }) {
  const pct = total > 0 ? Math.min((paid / total) * 100, 100) : 0;
  return (
    <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-700"
        style={{ width: `${pct}%`, backgroundColor: color }}
      />
    </div>
  );
}

// ── InvoiceDetailPage ─────────────────────────────────────────────────────────

const InvoiceDetailPage = () => {
  const { t, i18n } = useTranslation();
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const navigate = useNavigate();
  const lang = i18n.language;
  const isRtl = lang === 'ar';

  const { data: order, isLoading } = useQuery({
    queryKey: ['student-invoice-detail', orderNumber],
    queryFn: () =>
      // Backend: GET /student/payments/:number returns order + embedded bills[]
      api.get(`/student/payments/${orderNumber}`).then((res) => res.data?.data),
    enabled: !!orderNumber,
  });

  if (isLoading) {
    return <PageLoader message={t('common.loading')} />;
  }

  if (!order) {
    return (
      <div className="text-center py-16 text-muted-foreground">
        {t('common.notFound', { defaultValue: 'Invoice not found.' })}
      </div>
    );
  }

  const meta = getStatusMeta(order.status);
  const StatusIcon = meta.icon;
  const bills = order.bills ?? order.payments ?? [];

  return (
    <div className="space-y-8 pb-12">
      {/* Back button */}
      <button
        onClick={() => navigate('/payments')}
        className="flex items-center gap-2 px-5 py-2.5 rounded-2xl text-base font-bold text-muted-foreground hover:text-primary hover:bg-primary/5 transition-all group cursor-pointer"
      >
        <ArrowLeft
          className={`h-5 w-5 transition-transform ${isRtl ? 'rotate-180 group-hover:translate-x-1' : 'group-hover:-translate-x-1'}`}
        />
        {t('common.back')}
      </button>

      {/* Enrollment header card - LARGE REDESIGN */}
      <div className="rounded-[32px] border border-border bg-card  overflow-hidden">
        {/* Accent bar */}
        <div className="h-2 w-full" style={{ backgroundColor: meta.barColor }} />

        <div className="p-8 space-y-8">
          {/* Order number + status */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div
                className={`h-16 w-16 rounded-[22px] ${meta.bg} flex items-center justify-center shrink-0`}
              >
                <StatusIcon className={`h-8 w-8 ${meta.color}`} />
              </div>
              <div className="space-y-1">
                <p className="text-2xl font-black tracking-tight font-mono">{order.number}</p>
                <div className="flex items-center gap-2 text-sm text-muted-foreground font-bold">
                  <Calendar className="h-4 w-4" />
                  {formatDate(order.createdAt, lang)}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <span
                className={`px-5 py-2 rounded-2xl text-sm font-black uppercase tracking-wider ${meta.bg} ${meta.color} border ${meta.border} shadow-sm`}
              >
                {t(`common.${meta.label}`, { defaultValue: meta.label })}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Programs list */}
            {order.items?.length > 0 && (
              <div className="rounded-3xl bg-muted/30 p-6 space-y-4">
                <p className="text-xs font-black text-muted-foreground/50 uppercase tracking-widest flex items-center gap-2">
                  <Layers className="h-4 w-4" />
                  {t('student.billing.enrolledPrograms')}
                </p>
                <div className="space-y-3">
                  {order.items.map((item: any, i: number) => (
                    <div
                      key={i}
                      className="flex items-center justify-between gap-4 py-2 border-b border-border/50 last:border-0"
                    >
                      <p className="text-base font-bold truncate">
                        {item.program?.track?.name ??
                          item.program?.name ??
                          t('student.billing.dashboard.fallbackProgram')}
                      </p>
                      <p className="text-base font-black text-foreground/70 shrink-0">
                        {formatCurrency(item.amount ?? item.price ?? 0, lang)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Financial Summary */}
            <div className="flex flex-col justify-center space-y-4 px-2">
              <div className="flex items-center justify-between p-5 rounded-[24px] bg-primary/5 border border-primary/10">
                <span className="text-base font-bold text-primary/70">
                  {t('student.payments.amount')}
                </span>
                <span className="text-3xl font-black text-primary">
                  {formatCurrency(order.netTotal, lang)}
                </span>
              </div>

              <div className="space-y-3 px-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-muted-foreground">
                    {t('student.payments.paidAmount')}
                  </span>
                  <span className="text-lg font-black text-emerald-600 dark:text-emerald-400">
                    {formatCurrency(order.paidTotal, lang)}
                  </span>
                </div>
                {order.remaining > 0 && (
                  <div className="flex items-center justify-between pt-1 border-t border-dashed border-border">
                    <span className="text-sm font-bold text-muted-foreground">
                      {t('student.payments.remaining')}
                    </span>
                    <span className="text-lg font-black text-amber-600 dark:text-amber-400">
                      {formatCurrency(order.remaining, lang)}
                    </span>
                  </div>
                )}
                <div className="pt-2">
                  <ProgressBar
                    paid={order.paidTotal}
                    total={order.netTotal}
                    color={meta.barColor}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bills / Payment receipts */}
      {bills.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-black tracking-tight flex items-center gap-2 px-1">
            <Receipt className="h-5 w-5 text-primary" />
            {t('student.billing.paymentReceipts')}
          </h2>

          <div className="space-y-3">
            {bills.map((bill: any, i: number) => (
              <div
                key={bill._id ?? i}
                onClick={() =>
                  navigate(`/payments/${orderNumber}/bills/${bill.number ?? bill._id}`)
                }
                className="group flex items-center gap-4 p-4 rounded-2xl border border-border bg-card shadow-sm cursor-pointer transition-all hover:shadow-md hover:border-primary/20 hover:-translate-y-0.5 duration-300"
              >
                <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <CreditCard className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-black text-sm font-mono">{bill.number ?? `#${i + 1}`}</p>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                    {bill.createdAt && (
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(bill.createdAt, lang)}
                      </span>
                    )}
                    {bill.paymentMethod && (
                      <span className="font-bold">
                        {t(`common.${bill.paymentMethod.toLowerCase()}`, {
                          defaultValue: bill.paymentMethod,
                        })}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-end shrink-0">
                  <p className="font-black text-sm text-emerald-600 dark:text-emerald-400">
                    {formatCurrency(bill.amount ?? bill.paid ?? 0, lang)}
                  </p>
                  <ChevronRight
                    className={`h-4 w-4 text-muted-foreground/40 mx-auto mt-1 ${isRtl ? 'rotate-180' : ''} group-hover:text-primary transition-colors`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceDetailPage;
